/**
 * Supernal TTS Widget
 * Embeddable text-to-speech widget for blogs and websites
 */

import { SupernalTTSOptions } from './types';
import { AnalyticsManager } from './core/analytics';
import { CacheManager } from './core/cache';
import { UIComponentFactory } from './ui/components';
import { UIStateManager } from './ui/state-manager';
import { DevTools } from './ui/dev-tools';
import { AudioGenerator } from './audio/generator';
import { AudioPlayer } from './audio/player';
import { WidgetManager } from './widget-manager';
import { scrubText } from './core/text-processor';

// Re-export types for convenience
export * from './types';

export class SupernalTTS {
  // Configuration
  private apiUrl: string;
  private defaultVoice: string;
  private defaultProvider: string;
  private defaultSpeed: number;
  private autoHash: boolean;
  private cacheExpiry: number;
  private apiKey?: string;
  private showBranding: boolean;
  private version: string;
  private devMode: boolean;
  private clientSideSpeed: boolean;
  private generationMode: 'standard' | 'progressive' | 'realtime';
  private progressiveThreshold: number;

  // Module instances
  private analyticsManager: AnalyticsManager;
  private cacheManager: CacheManager;
  private uiFactory: UIComponentFactory;
  private uiStateManager: UIStateManager;
  private devTools?: DevTools;
  private audioGenerator: AudioGenerator;
  private audioPlayer: AudioPlayer;
  private widgetManager: WidgetManager;

  constructor(options: SupernalTTSOptions = {}) {
    // Initialize configuration
    this.apiUrl = options.apiUrl || 'https://www.tts.supernal.ai';
    this.defaultVoice = options.voice || 'default';
    this.defaultProvider = options.provider || 'openai';
    this.defaultSpeed = options.speed || 1.0;
    this.autoHash = options.autoHash !== false;
    this.cacheExpiry = options.cacheExpiry || 7 * 24 * 60 * 60 * 1000; // 7 days

    // Auto-detect API key from common environment variables if not explicitly provided
    // Supports: NEXT_PUBLIC_TTS_API_KEY, VITE_TTS_API_KEY, PUBLIC_TTS_API_KEY, REACT_APP_TTS_API_KEY
    this.apiKey = options.apiKey || this.detectApiKey();

    this.showBranding = options.showBranding !== false;
    this.version = options.version || '1.0.0';
    // devMode must be explicitly enabled now (not auto-enabled by localhost)
    this.devMode = options.devMode === true;
    this.clientSideSpeed = options.clientSideSpeed !== false; // Default to true for cost savings
    this.generationMode = options.generationMode || 'standard';
    this.progressiveThreshold = options.progressiveThreshold || 2000;

    // Initialize modules
    this.cacheManager = new CacheManager(this.cacheExpiry, this.apiUrl);

    this.analyticsManager = new AnalyticsManager(
      options.enableAnalytics !== false,
      this.apiUrl,
      this.version,
      this.devMode,
      options.referralCode
    );

    this.uiFactory = new UIComponentFactory(this.analyticsManager);
    this.uiStateManager = new UIStateManager();

    if (this.devMode) {
      this.devTools = new DevTools(this.cacheManager, this.devMode);
    }

    this.audioGenerator = new AudioGenerator(this.apiUrl, this.apiKey);
    this.audioPlayer = new AudioPlayer(this.clientSideSpeed, this.analyticsManager);

    this.widgetManager = new WidgetManager(
      {
        defaultVoice: this.defaultVoice,
        defaultProvider: this.defaultProvider,
        defaultSpeed: this.defaultSpeed,
        apiKey: this.apiKey,
        clientSideSpeed: this.clientSideSpeed,
        generationMode: this.generationMode,
        progressiveThreshold: this.progressiveThreshold
      },
      {
        cacheManager: this.cacheManager,
        uiFactory: this.uiFactory,
        uiStateManager: this.uiStateManager,
        audioGenerator: this.audioGenerator,
        audioPlayer: this.audioPlayer
      }
    );

    // Initialize
    this.init();
  }

  /**
   * Auto-detect API key from common framework environment variables
   * Checks in order: NEXT_PUBLIC_TTS_API_KEY, VITE_TTS_API_KEY, PUBLIC_TTS_API_KEY, REACT_APP_TTS_API_KEY
   */
  private detectApiKey(): string | undefined {
    if (typeof window === 'undefined') {
      return undefined; // Server-side, no auto-detection
    }

    // Check common framework patterns for client-side env vars
    const envVars = [
      'NEXT_PUBLIC_TTS_API_KEY',     // Next.js
      'VITE_TTS_API_KEY',             // Vite
      'PUBLIC_TTS_API_KEY',           // SvelteKit, Astro
      'REACT_APP_TTS_API_KEY'         // Create React App
    ];

    for (const varName of envVars) {
      try {
        // Try process.env (bundlers often expose this at build time)
        const processEnv = (globalThis as any).process?.env;
        if (processEnv?.[varName]) {
          console.log(`[TTS Widget] Auto-detected API key from process.env.${varName}`);
          return processEnv[varName];
        }

        // Try import.meta.env (Vite, modern bundlers)
        const importMetaEnv = (globalThis as any).import?.meta?.env;
        if (importMetaEnv?.[varName]) {
          console.log(`[TTS Widget] Auto-detected API key from import.meta.env.${varName}`);
          return importMetaEnv[varName];
        }
      } catch (e) {
        // Silently continue if env access fails
        continue;
      }
    }

    return undefined;
  }

  /**
   * Initialize the widget system
   */
  private init(): void {
    // Initialize widget manager
    this.widgetManager.init();

    // Add dev mode cache clear button if enabled and widgets exist
    if (this.devMode && this.devTools) {
      // Wait for DOM to be ready before checking for widgets
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => {
          const widgets = document.querySelectorAll('.supernal-tts-widget');
          if (widgets.length > 0) {
            this.devTools?.addClearCacheButton();
          }
        });
      } else {
        const widgets = document.querySelectorAll('.supernal-tts-widget');
        if (widgets.length > 0) {
          this.devTools.addClearCacheButton();
        }
      }
    }
  }

  // Public API methods
  public static init(options?: SupernalTTSOptions): SupernalTTS {
    if ((window as any).SupernalTTSInstance) {
      console.debug('SupernalTTS already initialized, returning existing instance');
      return (window as any).SupernalTTSInstance;
    }

    (window as any).SupernalTTSInstance = new SupernalTTS(options);
    return (window as any).SupernalTTSInstance;
  }

  public static getInstance(): SupernalTTS | undefined {
    return (window as any).SupernalTTSInstance;
  }

  public addWidget(element: HTMLElement | string, text: string, options: any = {}): void {
    this.widgetManager.addWidget(element, text, options);
  }

  // ========================================
  // Programmatic API for AI integration
  // ========================================

  /**
   * Play arbitrary text with TTS (for AI assistants to read selections, etc.)
   * @param text - Text to speak
   * @param options - Voice, speed, provider overrides
   * @returns Promise that resolves when audio completes
   */
  public async playText(text: string, options: { voice?: string; speed?: number; provider?: string } = {}): Promise<void> {
    const voice = options.voice || this.defaultVoice;
    const speed = options.speed || this.defaultSpeed;
    const provider = options.provider || this.defaultProvider;

    // Generate audio
    const response = await this.audioGenerator.generateAudio(text, { voice, speed, provider, apiKey: this.apiKey });

    // Create a temporary button for audio player state management
    const tempButton = document.createElement('button');
    tempButton.style.display = 'none';

    // Play audio with a simple state handler
    await this.audioPlayer.playAudio(
      response.audioUrl,
      tempButton,
      (state) => console.log('[TTS API] State:', state),
      () => {}, // No progress bar
      null,
      this.clientSideSpeed ? speed : undefined,
      response.hash
    );
  }

  /**
   * Play the current text selection (for "read selection" AI tools)
   * @returns Promise that resolves when audio completes
   */
  public async playSelection(): Promise<void> {
    const selection = window.getSelection();
    if (!selection || selection.toString().trim() === '') {
      throw new Error('No text selected');
    }

    const text = scrubText(selection.toString());
    return this.playText(text);
  }

  /**
   * Play text from a specific HTML element (for "read section" AI tools)
   * @param element - DOM element or CSS selector
   * @returns Promise that resolves when audio completes
   */
  public async playElement(element: HTMLElement | string): Promise<void> {
    const el = typeof element === 'string' ? document.querySelector<HTMLElement>(element) : element;

    if (!el) {
      throw new Error(`Element not found: ${element}`);
    }

    // Extract text content, scrubbing URLs and markdown
    const text = scrubText(el.textContent || '');

    if (!text.trim()) {
      throw new Error('Element contains no text');
    }

    return this.playText(text);
  }

  /**
   * Stop current playback
   */
  public stop(): void {
    this.audioPlayer.stopAudio(() => {});
  }

  /**
   * Get current playback state (for AI assistants to check status)
   */
  public getState(): { isPlaying: boolean; currentButton?: HTMLButtonElement; currentAudio?: HTMLAudioElement } {
    const currentButton = this.audioPlayer.getCurrentButton();
    return {
      isPlaying: currentButton !== null && this.audioPlayer.isPlayingOn(currentButton),
      currentButton: currentButton || undefined,
      currentAudio: this.audioPlayer.getCurrentAudio() || undefined
    };
  }
}

// Auto-initialize if configuration is found
if (typeof window !== 'undefined') {
  if (!(window as any).SupernalTTS) {
    (window as any).SupernalTTS = SupernalTTS;

    // Auto-scan for widgets on page load
    const autoScan = () => {
      // Check if already initialized
      const instance = (window as any).SupernalTTSInstance;
      if (!instance) {
        // Look for auto-init config in script tag
        const autoInitScript = document.querySelector<HTMLScriptElement>('script[data-supernal-tts-auto-init]');
        if (autoInitScript && autoInitScript.dataset) {
          const config = JSON.parse(autoInitScript.dataset.supernalTtsAutoInit || '{}');
          SupernalTTS.init(config);
        }
      }

      // Scan for widgets and auto-initialize them
      const widgets = document.querySelectorAll<HTMLElement>('.supernal-tts-widget');
      if (widgets.length > 0 && (window as any).SupernalTTSInstance) {
        const instance = (window as any).SupernalTTSInstance;
        widgets.forEach(widget => {
          // Skip if already initialized
          if (!widget.classList.contains('supernal-tts-widget-initialized')) {
            instance.widgetManager.setupWidget(widget);
          }
        });
      }
    };

    // Run auto-scan when DOM is ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', autoScan);
    } else {
      // DOM already loaded, run immediately
      autoScan();
    }
  } else {
    console.debug('SupernalTTS already loaded, skipping redeclaration');
  }
}

export default SupernalTTS;
